import { getGoogleLoginUrl as coreGetGoogleLoginUrl } from '@polaris/core/auth'
import { connectLitClient, litClient } from '@polaris/core/mpc'
import ky from 'ky'
import { apiClient } from '../client'
import { type MpcLoginRequest, mpcLogin } from './mpc'

interface GoogleLoginRequest {
  code: string
}

interface GoogleLoginResponse {
  access_token: string
  id_token: string
}

/** Returns a polaris access token. */
export function googleLogin({
  code,
}: GoogleLoginRequest): Promise<GoogleLoginResponse> {
  return apiClient
    .post('auth/login/google', {
      json: {
        redirect_uri: `${window.location.origin}/auth/callback/google`,
        code,
      },
    })
    .json<GoogleLoginResponse>()
}

// Types for lit-server auth credentials
interface ProviderInfo {
  keyID: string
  ipfsHash: string
}

interface SessionSigsMap {
  [key: string]: {
    sig: string
    derivedVia: string
    signedMessage: string
    address: string
    algo?: string
  }
}

export interface AuthCredentials {
  sessionSigs: SessionSigsMap
  providerInfo: {
    google?: ProviderInfo
    apple?: ProviderInfo
  }
}

/**
 * Exchange Google OAuth authorization code for access tokens using Lit Action
 * This replaces the backend call with a direct Lit Action execution
 */
export async function getGoogleTokenViaLitAction({
  code,
}: GoogleLoginRequest): Promise<GoogleLoginResponse> {
  // Import LIT_NETWORK dynamically to avoid SSR issues
  const { LIT_NETWORK } = await import('@lit-protocol/constants')

  // Fetch auth credentials from main server (which proxies to lit-server)
  const authCredentials = await apiClient
    .get('auth/credentials')
    .json<AuthCredentials>()

  const { sessionSigs, providerInfo } = authCredentials

  if (!providerInfo.google) {
    throw new Error('Google provider is not configured on the auth server')
  }

  const googleProvider = providerInfo.google

  // Validate we received session signatures from Lit servers
  const sessionSigValues = Object.values(sessionSigs)
  if (sessionSigValues.length === 0) {
    throw new Error('No session signatures received from Lit servers')
  }

  // Connect to the singleton Lit client (reuses existing connection if already connected)
  await connectLitClient()

  // Execute the Lit Action to sign in with Google
  const result = await litClient.executeJs({
    sessionSigs,
    ipfsId: googleProvider.ipfsHash,
    jsParams: {
      secret_id: googleProvider.keyID,
      litNetwork: LIT_NETWORK.Datil,
      sessionSig: sessionSigValues[0], // Get first session sig
      redirect_uri: `${window.location.origin}/auth/callback/google`,
      code,
      ipfsHash: googleProvider.ipfsHash,
    },
  })

  // Parse response - handle both string and object formats
  try {
    const tokenResponse =
      typeof result.response === 'string'
        ? JSON.parse(result.response)
        : result.response

    if (!tokenResponse.access_token || !tokenResponse.id_token) {
      throw new Error('Missing required tokens in response')
    }

    return {
      access_token: tokenResponse.access_token,
      id_token: tokenResponse.id_token,
    }
  } catch (error) {
    console.error('Failed to parse Lit Action response:', result, error)
    throw new Error('Google authentication failed. Please try again.')
  }
}

interface GetGoogleUserInfoRequest {
  accessToken: string
}

interface GetGoogleUserInfoResponse {
  sub: string
  name: string
  email: string
  picture: string
}

export function getGoogleUserInfo(payload: GetGoogleUserInfoRequest) {
  return ky
    .get<GetGoogleUserInfoResponse>(
      'https://www.googleapis.com/oauth2/v3/userinfo',
      {
        headers: {
          Authorization: `Bearer ${payload.accessToken}`,
        },
      },
    )
    .json()
}

export function mpcGoogleLogin(
  payload: MpcLoginRequest,
): ReturnType<typeof mpcLogin> {
  // Thin wrapper maintained for backwards-compatibility with existing imports.
  return mpcLogin(payload)
}

/** Returns a URL to start the Google OAuth2 flow. */
export function getGoogleLoginUrl(): string {
  const googleClientId = process.env.NEXT_PUBLIC_GOOGLE_CLIENT_ID
  if (!googleClientId) {
    throw new Error(
      'NEXT_PUBLIC_GOOGLE_CLIENT_ID is not defined in environment variables',
    )
  }
  const appBaseUrl = process.env.APP_BASE_URL
  if (!appBaseUrl) {
    throw new Error('APP_BASE_URL is not defined in environment variables')
  }
  return coreGetGoogleLoginUrl({ googleClientId, appBaseUrl })
}
